// frontend/src/pages/components/ExportJobsManager.tsx
//
// Sektion-Komponente für Sperrliste-Export-Jobs: Job-Liste (Table), CRUD,
// "Jetzt ausführen" (Run) und generischer Vorschau-Dialog.
// Wird in SperrlisteView.tsx als eigene Sektion eingehängt (bringt eigenen Paper mit).
import { useState, useEffect, useCallback, ReactNode } from 'react';
import {
  Box, Paper, Typography, Button,
  Table, TableHead, TableBody, TableRow, TableCell,
  Chip, IconButton, Tooltip, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar
} from '@mui/material';
import { Add, Edit, Delete, PlayArrow, Visibility, Download } from '@mui/icons-material';
import { getApiUrl, authFetch } from '../../config';
import ExportJobFormDialog from './ExportJobFormDialog';
import { QUELLE_LABELS, DATEN_LABELS } from './exportJobLabels';

// quelle kann aus dem GET als Array ODER als kommaseparierter String (mysql2-SET) kommen.
const normalizeQuelle = (raw: any): string[] => {
  if (Array.isArray(raw)) return raw;
  if (raw) return String(raw).split(',').filter(Boolean);
  return [];
};

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
};

interface PreviewData {
  jobName: string;
  header: string[];
  rows: string[][];
  total: number;
}

interface ExportJobsManagerProps {
  toolbarExtra?: ReactNode;
}

export default function ExportJobsManager({ toolbarExtra }: ExportJobsManagerProps = {}) {
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  // Form-Dialog
  const [formOpen, setFormOpen] = useState(false);
  const [formRecord, setFormRecord] = useState<any | null>(null);

  // Vorschau-Dialog
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Run-Status (welche Zeile gerade läuft)
  const [runningId, setRunningId] = useState<number | null>(null);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(getApiUrl('/sperrliste/export-jobs'));
      const data = await res.json();
      if (data.success) {
        setJobs(data.jobs || []);
      } else {
        setError(data.error || 'Laden fehlgeschlagen');
      }
    } catch (err) {
      setError((err as Error).message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  const handleFormSave = () => {
    setFormOpen(false);
    setFormRecord(null);
    loadJobs();
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Export-Job wirklich löschen?')) return;
    try {
      const res = await authFetch(getApiUrl(`/sperrliste/export-jobs/${id}`), { method: 'DELETE' });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Löschen fehlgeschlagen');
      loadJobs();
    } catch (err) {
      setSnackbar({ msg: (err as Error).message, severity: 'error' });
    }
  };

  const handleRun = async (id: number) => {
    setRunningId(id);
    try {
      const res = await authFetch(getApiUrl(`/sperrliste/export-jobs/${id}/run`), { method: 'POST' });
      const data = await res.json();
      if (data.success && data.status === 'success') {
        setSnackbar({
          msg: `${(data.row_count ?? 0).toLocaleString('de-DE')} Zeilen → ${data.file_path} (${data.status})`,
          severity: 'success',
        });
      } else {
        setSnackbar({
          msg: `Lauf fehlgeschlagen: ${data.error || data.status || 'unbekannt'}`,
          severity: 'error',
        });
      }
    } catch (err) {
      setSnackbar({ msg: (err as Error).message, severity: 'error' });
    }
    setRunningId(null);
    loadJobs();
  };

  const handleDownload = async (job: any) => {
    try {
      const res = await authFetch(getApiUrl('/sperrliste/export-jobs/' + job.id + '/download'));
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        const ct = res.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          try {
            const data = await res.json();
            if (data?.error) msg = data.error;
          } catch { /* ignore parse error, keep HTTP status */ }
        }
        setSnackbar({ msg: 'Download fehlgeschlagen: ' + msg, severity: 'error' });
        return;
      }
      const disposition = res.headers.get('content-disposition') || '';
      const m = disposition.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : ((job.last_run_file || '').split('/').pop() || 'export');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setSnackbar({ msg: (err as Error).message, severity: 'error' });
    }
  };

  const handlePreview = async (job: any) => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    setPreview(null);
    try {
      const res = await authFetch(getApiUrl(`/sperrliste/export-jobs/${job.id}/preview`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ limit: 50 }),
      });
      const data = await res.json();
      if (data.success) {
        // header/rows generisch — die Spalten ergeben sich aus quelle+daten des Jobs
        setPreview({
          jobName: job.name,
          header: data.header || [],
          rows: data.rows || [],
          total: data.total ?? 0,
        });
      } else {
        setSnackbar({ msg: `Vorschau fehlgeschlagen: ${data.error || 'unbekannt'}`, severity: 'error' });
        setPreviewOpen(false);
      }
    } catch (err) {
      setSnackbar({ msg: (err as Error).message, severity: 'error' });
      setPreviewOpen(false);
    }
    setPreviewLoading(false);
  };

  const isActive = (job: any): boolean => job.active !== false && job.active !== 0;

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h6">Export-Jobs</Typography>
        <Box sx={{ flexGrow: 1 }} />
        {toolbarExtra && <Box sx={{ mr: 1 }}>{toolbarExtra}</Box>}
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => { setFormRecord(null); setFormOpen(true); }}
        >
          Job
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Quelle</TableCell>
            <TableCell>Daten</TableCell>
            <TableCell>Letzter Lauf</TableCell>
            <TableCell>Aktiv</TableCell>
            <TableCell align="right">Aktionen</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {jobs.map(job => (
            <TableRow key={job.id} hover sx={{ opacity: isActive(job) ? 1 : 0.5 }}>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>{job.name}</Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {normalizeQuelle(job.quelle).map((q: string) => (
                    <Chip key={q} label={QUELLE_LABELS[q] || q} size="small" variant="outlined" />
                  ))}
                </Box>
              </TableCell>
              <TableCell>
                <Chip label={DATEN_LABELS[job.daten] || job.daten} size="small" variant="outlined" />
              </TableCell>
              <TableCell>
                {job.last_run_at ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">{formatDate(job.last_run_at)}</Typography>
                    {job.last_run_status && (
                      <Chip
                        label={job.last_run_status}
                        size="small"
                        color={job.last_run_status === 'success' ? 'success' : 'error'}
                        variant="outlined"
                      />
                    )}
                    {job.last_run_count != null && (
                      <Typography variant="caption" color="text.secondary">
                        {job.last_run_count.toLocaleString('de-DE')} Zeilen
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">—</Typography>
                )}
              </TableCell>
              <TableCell>
                <Chip
                  label={isActive(job) ? 'Ja' : 'Nein'}
                  size="small"
                  color={isActive(job) ? 'success' : 'default'}
                  variant="outlined"
                />
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Vorschau">
                  <IconButton size="small" onClick={() => handlePreview(job)}>
                    <Visibility fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Jetzt ausführen">
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleRun(job.id)}
                      disabled={runningId === job.id}
                    >
                      <PlayArrow fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Herunterladen">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleDownload(job)}
                      disabled={!job.last_run_file || job.last_run_status !== 'success'}
                    >
                      <Download fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Bearbeiten">
                  <IconButton size="small" onClick={() => { setFormRecord(job); setFormOpen(true); }}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Löschen">
                  <IconButton size="small" color="error" onClick={() => handleDelete(job.id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
          {jobs.length === 0 && !loading && (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">Keine Export-Jobs vorhanden</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Form-Dialog (Neu/Bearbeiten) */}
      <ExportJobFormDialog
        open={formOpen}
        record={formRecord}
        onClose={() => { setFormOpen(false); setFormRecord(null); }}
        onSave={handleFormSave}
      />

      {/* Vorschau-Dialog (generisch: header + rows) */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Vorschau{preview ? `: ${preview.jobName}` : ''}
        </DialogTitle>
        <DialogContent>
          {previewLoading && <Typography color="text.secondary">Lade Vorschau…</Typography>}
          {!previewLoading && preview && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {preview.rows.length} von {preview.total.toLocaleString('de-DE')} Zeilen
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {preview.header.map((h, i) => (
                        <TableCell key={i} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.rows.map((row, ri) => (
                      <TableRow key={ri}>
                        {row.map((cell, ci) => (
                          <TableCell key={ci} sx={{ whiteSpace: 'nowrap' }}>{cell}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                    {preview.rows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={Math.max(preview.header.length, 1)} align="center" sx={{ py: 3 }}>
                          <Typography color="text.secondary">Keine Zeilen</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Run-/Aktionsergebnis */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} sx={{ width: '100%' }}>
            {snackbar.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Paper>
  );
}
